import { ActivePaidSubscriptionDataMapper } from 'modules/organizer/domain/data-mappers/active-paid-subscription.data-mapper';
import { ActivePaidSubscription } from 'modules/organizer/domain/interfaces/active-paid-subscription';
import { ActivePaidSubscriptionDto } from 'modules/organizer/domain/interfaces/active-paid-subscription-dto';
import { ActiveTournamentPlan } from 'modules/organizer/domain/interfaces/active-tournament-plan';
import { baseApi } from 'shared/api/base-api';

/**
 * Активный тариф организации и оплаченная подписка на него — одним запросом.
 *
 * Раньше это были два источника: этот эндпоинт (только тариф, страница настроек)
 * и thunk `fetchActiveTournamentPlan` в модуле `organizers`, который параллельно
 * дёргал тариф и подписку для карточки организатора в админке. Оба поля —
 * верхнего уровня, поэтому один документ покрывает обе страницы.
 */
const activeTournamentPlanQuery = `#graphql
  query ActiveTournamentPlan($tournamentId: Int!) {
    activeTournamentPlan(tournamentId: $tournamentId) {
      id
      name
      priceInMinorUnits
      period
    }
    activePaidSubscription(tournamentId: $tournamentId) {
      id
      ownerId
      activeFrom
      activeTo
      purchaseType
    }
  }
`;

interface ActiveTournamentPlanResponseDto {
  activeTournamentPlan: ActiveTournamentPlan | null;
  activePaidSubscription: ActivePaidSubscriptionDto | null;
}

/** Тариф вместе с подпиской: даты «активен с/по» лежат именно в подписке. */
export interface ActiveTournamentPlanWithSubscription extends ActiveTournamentPlan {
  subscription: ActivePaidSubscription | null;
}

export function mapActiveTournamentPlanResponse(raw: ActiveTournamentPlanResponseDto): ActiveTournamentPlanWithSubscription | null {
  if (!raw.activeTournamentPlan) {
    return null;
  }

  return {
    ...raw.activeTournamentPlan,
    subscription: new ActivePaidSubscriptionDataMapper().decode(raw.activePaidSubscription),
  };
}

/**
 * Только подписка — без тарифа. `getActiveTournamentPlan` её не заменяет: без активного тарифа он отдаёт `null` и
 * теряет подписку, а уведомление об окончании подписки и страницы локаций читают именно её.
 */
const activePaidSubscriptionQuery = `#graphql
  query ActivePaidSubscription($tournamentId: Int!) {
    activePaidSubscription(tournamentId: $tournamentId) {
      id
      ownerId
      activeFrom
      activeTo
      purchaseType
    }
  }
`;

export function mapActivePaidSubscriptionResponse(raw: {
  activePaidSubscription: ActivePaidSubscriptionDto | null;
}): ActivePaidSubscription | null {
  return new ActivePaidSubscriptionDataMapper().decode(raw?.activePaidSubscription ?? null);
}

const organizerPlanApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** Аргумент — id организации (backend `tournamentId`). Тег тот же, что у тарифа: покупка тарифа сбрасывает оба. */
    getActivePaidSubscription: build.query<ActivePaidSubscription | null, number>({
      query: (organizerId) => ({
        query: activePaidSubscriptionQuery,
        variables: { tournamentId: organizerId },
      }),
      transformResponse: mapActivePaidSubscriptionResponse,
      providesTags: (_res, _err, organizerId) => [{ type: 'Organizer', id: organizerId }],
    }),
    getActiveTournamentPlan: build.query<ActiveTournamentPlanWithSubscription | null, { tournamentId: number }>({
      query: ({ tournamentId }) => ({
        query: activeTournamentPlanQuery,
        variables: { tournamentId },
      }),
      transformResponse: mapActiveTournamentPlanResponse,
      providesTags: (_res, _err, { tournamentId }) => [{ type: 'Organizer', id: tournamentId }],
    }),
  }),
  overrideExisting: false,
});

export const { useGetActiveTournamentPlanQuery, useGetActivePaidSubscriptionQuery } = organizerPlanApi;

export default organizerPlanApi;
